// src/pages/CompanyCreationPage.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Autocomplete,
  useTheme,
  alpha,
  Fade,
  Slide,
  Zoom,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Home as HomeIcon,
  LocationCity as LocationCityIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  CalendarMonth as CalendarIcon,
  Group as GroupIcon,
  AccountBalance as AccountBalanceIcon,
  Public as PublicIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,

  Apartment as ApartmentIcon,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  background: '#ffffff',
  position: 'relative'
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: 24,
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.15)}`,
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  maxWidth: 850,
  margin: '0 auto',
  transform: 'scale(0.95)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'scale(0.95) translateY(-8px)',
    boxShadow: `0 32px 80px ${alpha(theme.palette.primary.main, 0.25)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 6,
    background: `linear-gradient(90deg, 
         ${theme.palette.primary.main}, 
         ${theme.palette.secondary.main}
    )`
  }
}));

const HeaderCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 20,
  marginBottom: theme.spacing(4),
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '150px',
    height: '150px',
    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
    borderRadius: '50%',
    transform: 'translate(30%, -30%)',
    zIndex: 0
  }
}));

const StepCard = styled(Card)(({ theme, active }) => ({
  borderRadius: 20,
  background: 'rgba(255, 255, 255, 0.98)',
  border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
  boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.2)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 16,
    background: 'rgba(255, 255, 255, 1)',
    fontSize: '1rem',
    minHeight: 64,
    paddingLeft: 64,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '& .MuiOutlinedInput-input': {
      padding: '20px 16px',
      paddingLeft: 0,
      fontWeight: 500,
      fontSize: '1rem'
    },
    '&.MuiInputBase-multiline': {
      padding: '8px 16px 8px 64px',
      alignItems: 'flex-start',
      '& .MuiOutlinedInput-input': {
        padding: '12px 0',
        lineHeight: 1.5
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '&.Mui-focused': {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`
    },
    '&:hover': {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.6)',
    left: 64,
    transition: 'all 0.3s ease',
    backgroundColor: '#ffffff',
    padding: '0 8px',
    marginLeft: '-8px',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
      fontWeight: 500,
      backgroundColor: '#ffffff'
    },
    '&.MuiInputLabel-shrink': {
      left: 64,
      transform: 'translate(-14px, -9px) scale(0.75)',
      backgroundColor: '#ffffff'
    }
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 64,
    marginTop: 8,
    fontWeight: 500,
    fontSize: '0.875rem'
  },
  marginBottom: theme.spacing(3)
}));

const StyledSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 16,
    background: 'rgba(255, 255, 255, 1)',
    fontSize: '1rem',
    minHeight: 64,
    paddingLeft: 64,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '&.Mui-focused': {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`
    },
    '&:hover': {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.6)',
    left: 64,
    transition: 'all 0.3s ease',
    backgroundColor: '#ffffff',
    padding: '0 8px',
    marginLeft: '-8px',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
      fontWeight: 500,
      backgroundColor: '#ffffff'
    },
    '&.MuiInputLabel-shrink': {
      left: 64,
      transform: 'translate(-14px, -9px) scale(0.75)',
      backgroundColor: '#ffffff'
    }
  },
  marginBottom: theme.spacing(3),
  width: '100%'
}));

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 16,
    background: 'rgba(255, 255, 255, 1)',
    fontSize: '1rem',
    minHeight: 64,
    paddingLeft: 64,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '& .MuiOutlinedInput-input': {
      padding: '20px 16px',
      paddingLeft: 0,
      fontWeight: 500,
      fontSize: '1rem'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '&.Mui-focused': {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`
    },
    '&:hover': {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.6)',
    left: 64,
    transition: 'all 0.3s ease',
    backgroundColor: '#ffffff',
    padding: '0 8px',
    marginLeft: '-8px',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
      fontWeight: 500,
      backgroundColor: '#ffffff'
    },
    '&.MuiInputLabel-shrink': {
      left: 64,
      transform: 'translate(-14px, -9px) scale(0.75)',
      backgroundColor: '#ffffff'
    }
  },
  marginBottom: theme.spacing(3),
  width: '100%'
}));

const IconWrapper = styled(Box)(({ theme, color = 'primary' }) => ({
  width: 44,
  height: 44,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  flexShrink: 0,
  position: 'absolute',
  left: 12,
  top: 12,
  zIndex: 10,
  pointerEvents: 'none',
  transition: 'all 0.3s ease',
  '& svg': {
    fontSize: 22,
    color: theme.palette.primary.main
  }
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2, 6),
  fontWeight: 500,
  fontSize: '1.1rem',
  textTransform: 'none',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: theme.palette.primary.contrastText,
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`
  },
  '&:disabled': {
    background: alpha(theme.palette.grey[400], 0.5),
    color: theme.palette.grey[600],
    boxShadow: 'none'
  }
}));

const BackButton = styled(Button)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2, 4),
  fontWeight: 500,
  fontSize: '1.1rem',
  textTransform: 'none',
  border: `2px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  color: theme.palette.text.primary,
  background: 'white',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    border: `2px solid ${theme.palette.primary.main}`,
    background: alpha(theme.palette.primary.main, 0.05),
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`
  }
}));

const GlowWorksHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(6),
  position: 'relative',
  '& .logo-text': {
    fontSize: '4rem',
    fontWeight: 500,
    color: 'rgba(18, 18, 18, 0.89)',
    letterSpacing: '-0.02em',
    filter: 'drop-shadow(0 4px 12px rgba(99, 99, 102, 0.3))',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
      filter: 'drop-shadow(0 8px 20px rgba(99, 99, 102, 0.4))'
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '120%',
    height: '120%',
    background: `radial-gradient(ellipse, ${alpha('#636366', 0.1)} 0%, transparent 70%)`,
    borderRadius: '50%',
    zIndex: -1,
    animation: 'pulse 3s ease-in-out infinite'
  },
  '@keyframes pulse': {
    '0%, 100%': {
      transform: 'translate(-50%, -50%) scale(1)',
      opacity: 1
    },
    '50%': {
      transform: 'translate(-50%, -50%) scale(1.1)',
      opacity: 0.6
    }
  }
}));

const VerticalProgressBar = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginRight: theme.spacing(0),
  minWidth: 180
}));

const VerticalProgressLine = styled(Box)(({ theme, progress }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  top: 50,
  bottom: 100,
  width: 6,
  background: alpha(theme.palette.grey[300], 0.5),
  borderRadius: 3,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: `${progress}%`,
    background: '#424242',
    borderRadius: 3,
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `0 6px 18px ${alpha('#424242', 0.3)}`
  }
}));

const StepIndicator = styled(Box)(({ theme, active, completed }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  zIndex: 5,
  cursor: 'pointer',
  marginBottom: theme.spacing(6),
  transition: 'all 0.3s ease'
}));

const StepCircle = styled(Box)(({ theme, active, completed }) => ({
  width: 100,
  height: 100,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: completed 
    ? '#4CAF50' 
    : active 
    ? '#000000'
    : `linear-gradient(135deg, ${theme.palette.grey[300]}, ${theme.palette.grey[400]})`,
  color: (completed || active) ? 'white' : theme.palette.grey[600],
  fontWeight: 500,
  fontSize: '1.1rem',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: (completed || active) 
    ? `0 12px 36px ${alpha((completed ? theme.palette.success : theme.palette.primary).main, 0.3)}` 
    : `0 6px 20px ${alpha(theme.palette.grey[400], 0.2)}`,
  border: active ? `3px solid ${alpha(theme.palette.primary.light, 0.5)}` : 'none',
  transform: active ? 'scale(1.1)' : 'scale(1)',
  position: 'relative',
  zIndex: 4,
  '&:hover': {
    transform: active ? 'scale(1.15)' : 'scale(1.05)',
    boxShadow: (completed || active) 
      ? `0 16px 48px ${alpha((completed ? theme.palette.success : theme.palette.primary).main, 0.4)}` 
      : `0 10px 28px ${alpha(theme.palette.grey[400], 0.3)}`
  },
  '& svg': {
    fontSize: 30,
    transition: 'all 0.3s ease'
  }
}));

const CustomStepLabel = styled(Typography)(({ theme, active, completed }) => ({
  marginTop: theme.spacing(3),
  fontSize: '1rem',
  fontWeight: 500,
  color: (completed || active) ? theme.palette.text.primary : theme.palette.text.secondary,
  transition: 'all 0.3s ease',
  textAlign: 'center',
  maxWidth: 140,
  lineHeight: 1.3,
  wordWrap: 'break-word',
  background: '#ffffff',
  padding: theme.spacing(1.5, 2),
  borderRadius: 12,
  position: 'relative',
  zIndex: 3,
  border: 'none',
  boxShadow: 'none'
}));

const AnimatedStepContent = styled(Box)(({ theme }) => ({
  minHeight: 500,
  opacity: 0,
  transform: 'translateX(40px)',
  animation: 'slideInFromRight 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards',
  '@keyframes slideInFromRight': {
    '0%': { opacity: 0, transform: 'translateX(40px)' },
    '100%': { opacity: 1, transform: 'translateX(0)' }
  }
}));

const countries = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' }
];

const statesByCountry = {
  'US': [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ],
  'CA': [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 
    'Quebec', 'Saskatchewan', 'Yukon'
  ],
  'GB': [
    'England', 'Scotland', 'Wales', 'Northern Ireland'
  ],
  'AU': [
    'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland', 
    'South Australia', 'Tasmania', 'Victoria', 'Western Australia'
  ]
};

const industries = [
  'Technology', 'Healthcare', 'Finance', 'Real Estate', 'Education', 'Manufacturing',
  'Retail', 'Hospitality', 'Construction', 'Transportation', 'Agriculture', 'Energy',
  'Media & Entertainment', 'Legal Services', 'Consulting', 'Non-Profit', 'Other'
];

const businessTypes = [
  'Limited Liability Company (LLC)',
  'Corporation (C-Corp)',
  'S Corporation (S-Corp)',
  'Partnership',
  'Sole Proprietorship',
  'Non-Profit Organization',
  'Professional Corporation (PC)',
  'Limited Partnership (LP)',
  'Other'
];

const employeeRanges = [
  '1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'
];

const currencies = [
  { code: 'USD', label: 'US Dollar ($)', symbol: '$' },
  { code: 'EUR', label: 'Euro (€)', symbol: '€' },
  { code: 'GBP', label: 'British Pound (£)', symbol: '£' },
  { code: 'CAD', label: 'Canadian Dollar (C$)', symbol: 'C$' },
  { code: 'AUD', label: 'Australian Dollar (A$)', symbol: 'A$' }
];

const CompanyCreationPage = ({ onCompanyCreated, onCancel }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  
  const [companyInfo, setCompanyInfo] = useState({
    // Basic Info
    name: '',
    legal_name: '',
    industry: '',
    business_type: '',
    business_license: '',
    founded_date: '',
    employee_count: '',
    description: '',
    
    // Address
    address: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    
    // Contact
    phone: '',
    fax: '',
    email: '',
    website: '',
    
    // Primary Contact
    contact_person_name: '',
    contact_person_title: '',
    contact_person_phone: '',
    contact_person_email: '',
    

    
    // Business Settings
    business_hours: '',
    time_zone: 'America/New_York',

    default_currency: 'USD',
    default_payment_terms: 30,
    invoice_prefix: 'INV',
    quote_prefix: 'QUO'
  });
  
  const [loading, setLoading] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [error, setError] = useState(null);
  const [existingCompany, setExistingCompany] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const checkForExistingCompany = async () => {
      try {
        setCheckingExisting(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          // User not authenticated - this shouldn't happen as MainApp should handle it
          console.error("No authenticated user in CompanyCreationPage");
          setCheckingExisting(false);
          return;
        }
        
        // Use the RLS-safe function instead of direct table access
        const { data, error } = await supabase.rpc('check_user_existing_company');
          
        if (error) throw error;
        
        if (data && data.length > 0) {
          // Fetch full company details if one exists
          const { data: companyData, error: companyError } = await supabase
            .from('companies')
            .select('*')
            .eq('id', data[0].company_id)
            .single();
            
          if (companyError) throw companyError;
          
          setExistingCompany(companyData);
          onCompanyCreated(companyData);
        }
      } catch (err) {
        setError("Failed to check existing company: " + err.message);
      } finally {
        setCheckingExisting(false);
      }
    };
    
    checkForExistingCompany();
  }, [onCompanyCreated]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo(prev => {
      const updated = { ...prev, [name]: value };
      
      // Clear state when country changes
      if (name === 'country') {
        updated.state = '';
      }
      
      return updated;
    });
    
    // Clear errors when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 0: // Basic Info
        if (!companyInfo.name.trim()) errors.name = "Company name is required";
        if (!companyInfo.legal_name.trim()) errors.legal_name = "Legal name is required";
        if (!companyInfo.industry) errors.industry = "Industry is required";
        if (!companyInfo.business_type) errors.business_type = "Business type is required";
        break;
      case 1: // Address
        if (!companyInfo.address.trim()) errors.address = "Address is required";
        if (!companyInfo.city.trim()) errors.city = "City is required";
        if (!companyInfo.state.trim()) errors.state = "State is required";
        if (!companyInfo.zip_code.trim()) errors.zip_code = "ZIP Code is required";
        if (!companyInfo.country.trim()) errors.country = "Country is required";
        break;
      case 2: // Contact
        if (!companyInfo.phone.trim()) errors.phone = "Phone number is required";
        if (!companyInfo.email.trim()) errors.email = "Email address is required";
        else if (!/\S+@\S+\.\S+/.test(companyInfo.email)) {
          errors.email = "Please enter a valid email address";
        }
        if (!companyInfo.website.trim()) errors.website = "Website is required";
        if (!companyInfo.contact_person_name.trim()) errors.contact_person_name = "Contact name is required";
        if (!companyInfo.contact_person_title.trim()) errors.contact_person_title = "Contact title is required";
        if (!companyInfo.contact_person_phone.trim()) errors.contact_person_phone = "Contact phone is required";
        if (!companyInfo.contact_person_email.trim()) errors.contact_person_email = "Contact email is required";
        else if (!/\S+@\S+\.\S+/.test(companyInfo.contact_person_email)) {
          errors.contact_person_email = "Please enter a valid contact email address";
        }
        break;
      case 3: // Additional Details (optional step)
        break;
      case 4: // Settings
        if (!companyInfo.default_currency.trim()) errors.default_currency = "Default currency is required";
        if (!companyInfo.default_payment_terms || companyInfo.default_payment_terms === '' || companyInfo.default_payment_terms === 0) errors.default_payment_terms = "Default payment terms is required";
        if (!companyInfo.invoice_prefix.trim()) errors.invoice_prefix = "Invoice prefix is required";
        if (!companyInfo.quote_prefix.trim()) errors.quote_prefix = "Quote prefix is required";
        break;
      default:
        break;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Pure validation for render (no state updates)
  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return (
          companyInfo.name.trim() &&
          companyInfo.legal_name.trim() &&
          companyInfo.industry &&
          companyInfo.business_type
        );
      case 1:
        return (
          companyInfo.address.trim() &&
          companyInfo.city.trim() &&
          companyInfo.state.trim() &&
          companyInfo.zip_code.trim() &&
          companyInfo.country.trim()
        );
      case 2:
        const emailValid = companyInfo.email && /\S+@\S+\.\S+/.test(companyInfo.email);
        const contactEmailValid = companyInfo.contact_person_email && /\S+@\S+\.\S+/.test(companyInfo.contact_person_email);
        return (
          companyInfo.phone.trim() && 
          emailValid && 
          companyInfo.website.trim() &&
          companyInfo.contact_person_name.trim() &&
          companyInfo.contact_person_title.trim() &&
          companyInfo.contact_person_phone.trim() &&
          contactEmailValid
        );
      case 3:
        return true; // Optional step
      case 4:
        return (
          companyInfo.default_currency.trim() &&
          companyInfo.default_payment_terms &&
          companyInfo.default_payment_terms !== '' &&
          companyInfo.default_payment_terms !== 0 &&
          companyInfo.invoice_prefix.trim() &&
          companyInfo.quote_prefix.trim()
        );
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    
    // Validate all required steps before creating company
    const allStepsValid = [0, 1, 2, 4].every(stepIndex => {
      const stepValid = validateStep(stepIndex);
      return stepValid;
    });
    
    if (!allStepsValid) {
      setError("Please fill in all required fields in all steps before creating your company.");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      // Check for existing company using RLS-safe function
      const { data: existingCheck, error: checkError } = await supabase.rpc('check_user_existing_company');
        
      if (checkError) throw checkError;
      
      if (existingCheck && existingCheck.length > 0) {
        throw new Error("You already have a company. Only one company allowed per account.");
      }
      
      const companyData = {
        ...companyInfo,
        created_by: user.id
      };
      
      // Clean up empty fields but preserve required ones
      Object.keys(companyData).forEach(key => {
        if (companyData[key] === '' || companyData[key] === null || companyData[key] === undefined) {
          delete companyData[key];
        }
      });
      
      const { data, error } = await supabase
        .from('companies')
        .insert(companyData)
        .select()
        .single();
        
      if (error) throw error;
      
      // Automatically make the company creator the Super Admin
      try {
        const { error: ownerError } = await supabase.rpc('create_company_owner', {
          p_company_id: data.id
        });
        
        if (ownerError) {
          console.warn('Could not create company owner role:', ownerError.message);
          // Don't throw error here, company was created successfully
        }
      } catch (ownerErr) {
        console.warn('Could not create company owner role:', ownerErr.message);
        // Don't throw error here, company was created successfully
      }
      
      onCompanyCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (checkingExisting) {
    return (
      <PageContainer>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 3,
          p: 6,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.15)}`
        }}>
          <CircularProgress size={64} thickness={4} sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            Loading your workspace...
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  const steps = [
    {
      label: 'Business Information',
      icon: <BusinessIcon />,
      description: 'Tell us about your business',
      color: 'primary',
      fields: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ position: 'relative', width: '100%', overflow: 'visible' }}>
            <IconWrapper>
              <BusinessIcon fontSize="medium" />
            </IconWrapper>
            <StyledTextField 
              label="Company Name" 
              name="name"
              variant="outlined"
              fullWidth
              value={companyInfo.name} 
              onChange={handleInputChange} 
              required
              error={!!formErrors.name}
              helperText={formErrors.name}
              disabled={loading}
              placeholder="Enter your company name"
            />
          </Box>
          <Box sx={{ position: 'relative', width: '100%', overflow: 'visible' }}>
            <IconWrapper>
              <PersonIcon fontSize="medium" />
            </IconWrapper>
            <StyledTextField 
              label="Legal Name" 
              name="legal_name"
              variant="outlined"
              fullWidth
              value={companyInfo.legal_name} 
              onChange={handleInputChange} 
              required
              error={!!formErrors.legal_name}
              helperText={formErrors.legal_name}
              disabled={loading}
              placeholder="Legal business name"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
            <Box sx={{ position: 'relative', flex: 1, overflow: 'visible' }}>
              <IconWrapper>
                <AccountBalanceIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField
                label="Industry"
                name="industry"
                variant="outlined"
                fullWidth
                value={companyInfo.industry}
                onChange={handleInputChange}
                required
                error={!!formErrors.industry}
                helperText={formErrors.industry}
                disabled={loading}
                placeholder="e.g. Technology, Healthcare, Finance"
              />
            </Box>
            <Box sx={{ position: 'relative', flex: 1, overflow: 'visible' }}>
              <IconWrapper>
                <WorkIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField
                label="Business Type"
                name="business_type"
                variant="outlined"
                fullWidth
                value={companyInfo.business_type}
                onChange={handleInputChange}
                required
                error={!!formErrors.business_type}
                helperText={formErrors.business_type}
                disabled={loading}
                placeholder="e.g. LLC, Corporation, Partnership"
              />
            </Box>
          </Box>
          <Box sx={{ position: 'relative', width: '100%', overflow: 'visible' }}>
            <IconWrapper>
              <DescriptionIcon fontSize="medium" />
            </IconWrapper>
            <StyledTextField 
              label="Description" 
              name="description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={companyInfo.description} 
              onChange={handleInputChange} 
              disabled={loading}
              placeholder="Brief description of your business..."
            />
          </Box>
        </Box>
      )
    },
    {
      label: 'Business Address',
      icon: <HomeIcon />,
      description: 'Where is your business located?',
      color: 'secondary',
      fields: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <IconWrapper>
              <HomeIcon fontSize="medium" />
            </IconWrapper>
            <StyledTextField 
              label="Street Address" 
              name="address"
              variant="outlined"
              fullWidth
              value={companyInfo.address} 
              onChange={handleInputChange} 
              required
              error={!!formErrors.address}
              helperText={formErrors.address}
              disabled={loading}
              placeholder="123 Business Street"
            />
          </Box>
          <Box sx={{ position: 'relative' }}>
            <IconWrapper>
              <ApartmentIcon fontSize="medium" />
            </IconWrapper>
            <StyledTextField 
              label="Address Line 2" 
              name="address_2"
              variant="outlined"
              fullWidth
              value={companyInfo.address_2} 
              onChange={handleInputChange} 
              disabled={loading}
              placeholder="Suite, Unit, etc."
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <LocationCityIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="City" 
                name="city"
                variant="outlined"
                fullWidth
                value={companyInfo.city} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.city}
                helperText={formErrors.city}
                disabled={loading}
              />
            </Box>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <LocationCityIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="ZIP Code" 
                name="zip_code"
                variant="outlined"
                fullWidth
                value={companyInfo.zip_code} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.zip_code}
                helperText={formErrors.zip_code}
                disabled={loading}
              />
            </Box>
          </Box>
          <Box sx={{ position: 'relative' }}>
            <IconWrapper>
              <PublicIcon fontSize="medium" />
            </IconWrapper>
            <StyledSelect fullWidth>
              <InputLabel>Country</InputLabel>
              <Select
                name="country"
                value={companyInfo.country}
                onChange={handleInputChange}
                required
                error={!!formErrors.country}
                label="Country"
                disabled={loading}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>
                ))}
              </Select>
            </StyledSelect>
          </Box>
          {statesByCountry[companyInfo.country] && (
            <Box sx={{ position: 'relative' }}>
              <IconWrapper>
                <PublicIcon fontSize="medium" />
              </IconWrapper>
              <Autocomplete
                options={statesByCountry[companyInfo.country]}
                value={companyInfo.state || null}
                onChange={(event, newValue) => {
                  handleInputChange({
                    target: {
                      name: 'state',
                      value: newValue || ''
                    }
                  });
                }}
                filterOptions={(options, { inputValue }) =>
                  options.filter(option =>
                    option.toLowerCase().includes(inputValue.toLowerCase())
                  )
                }
                disabled={loading}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label="State/Province"
                    required
                    error={!!formErrors.state}
                    helperText={formErrors.state}
                    placeholder="Search for state/province..."
                    fullWidth
                  />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingLeft: '64px !important',
                    '& .MuiAutocomplete-input': {
                      paddingLeft: '0 !important'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    left: '64px !important',
                    '&.MuiInputLabel-shrink': {
                      left: '64px !important',
                      transform: 'translate(-14px, -9px) scale(0.75) !important'
                    }
                  },
                  marginBottom: 3
                }}
                ListboxProps={{
                  sx: {
                    maxHeight: 200,
                    '& .MuiAutocomplete-option': {
                      fontSize: '1rem',
                      fontWeight: 500,
                      padding: '12px 16px',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08)
                      },
                      '&.Mui-focused': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12)
                      }
                    }
                  }
                }}
              />
            </Box>
          )}
        </Box>
      )
    },
    {
      label: 'Contact Information',
      icon: <PhoneIcon />,
      description: 'How can customers reach you?',
      color: 'success',
      fields: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <PhoneIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Phone Number" 
                name="phone"
                variant="outlined"
                fullWidth
                value={companyInfo.phone} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                disabled={loading}
                placeholder="(123) 456-7890"
              />
            </Box>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <PhoneIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Fax Number" 
                name="fax"
                variant="outlined"
                fullWidth
                value={companyInfo.fax} 
                onChange={handleInputChange} 
                disabled={loading}
                placeholder="(123) 456-7891"
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <EmailIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Email Address" 
                name="email"
                type="email"
                variant="outlined"
                fullWidth
                value={companyInfo.email} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.email}
                helperText={formErrors.email}
                disabled={loading}
                placeholder="contact@yourcompany.com"
              />
            </Box>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <LanguageIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Website" 
                name="website"
                variant="outlined"
                fullWidth
                value={companyInfo.website} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.website}
                helperText={formErrors.website}
                disabled={loading}
                placeholder="https://www.yourcompany.com"
              />
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }}>
            <Chip 
              label="Primary Contact Person" 
              sx={{ 
                fontWeight: 500,
                fontSize: '0.95rem',
                bgcolor: alpha(theme.palette.info.main, 0.08),
                color: 'info.main',
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }} 
            />
          </Divider>
          
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <PersonIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Contact Name" 
                name="contact_person_name"
                variant="outlined"
                fullWidth
                value={companyInfo.contact_person_name} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.contact_person_name}
                helperText={formErrors.contact_person_name}
                disabled={loading}
                placeholder="John Doe"
              />
            </Box>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <WorkIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Title/Position" 
                name="contact_person_title"
                variant="outlined"
                fullWidth
                value={companyInfo.contact_person_title} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.contact_person_title}
                helperText={formErrors.contact_person_title}
                disabled={loading}
                placeholder="CEO"
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <PhoneIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Contact Phone" 
                name="contact_person_phone"
                variant="outlined"
                fullWidth
                value={companyInfo.contact_person_phone} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.contact_person_phone}
                helperText={formErrors.contact_person_phone}
                disabled={loading}
                placeholder="(123) 456-7890"
              />
            </Box>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <IconWrapper>
                <EmailIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Contact Email" 
                name="contact_person_email"
                type="email"
                variant="outlined"
                fullWidth
                value={companyInfo.contact_person_email} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.contact_person_email}
                helperText={formErrors.contact_person_email}
                disabled={loading}
                placeholder="john@yourcompany.com"
              />
            </Box>
          </Box>
        </Box>
      )
    },
    {
      label: 'Business Details',
      icon: <WorkIcon />,
      description: 'Additional business information (optional)',
      color: 'info',
      fields: (
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ position: 'relative' }}>
              <IconWrapper>
                <CalendarIcon fontSize="medium" />
              </IconWrapper>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  label="Founded Date"
                  value={companyInfo.founded_date ? dayjs(companyInfo.founded_date) : null}
                  onChange={(newValue) => {
                    handleInputChange({
                      target: {
                        name: 'founded_date',
                        value: newValue ? newValue.format('YYYY-MM-DD') : ''
                      }
                    });
                  }}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ position: 'relative' }}>
              <IconWrapper>
                <GroupIcon fontSize="medium" />
              </IconWrapper>
              <StyledSelect fullWidth>
                <InputLabel>Employee Count</InputLabel>
                <Select
                  name="employee_count"
                  value={companyInfo.employee_count}
                  onChange={handleInputChange}
                  label="Employee Count"
                >
                  {employeeRanges.map((range) => (
                    <MenuItem key={range} value={range}>{range} employees</MenuItem>
                  ))}
                </Select>
              </StyledSelect>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ position: 'relative' }}>
              <IconWrapper>
                <AccountBalanceIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Business License Number" 
                name="business_license"
                variant="outlined"
                fullWidth
                value={companyInfo.business_license} 
                onChange={handleInputChange} 
                disabled={loading}
                placeholder="Business license number"
              />
            </Box>
          </Grid>
          

        </Grid>
      )
    },
    {
      label: 'Business Settings',
      icon: <SettingsIcon />,
      description: 'Configure your business preferences',
      color: 'warning',
      fields: (
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ position: 'relative' }}>
              <IconWrapper>
                <MoneyIcon fontSize="medium" />
              </IconWrapper>
              <StyledSelect fullWidth>
                <InputLabel>Default Currency</InputLabel>
                <Select
                  name="default_currency"
                  value={companyInfo.default_currency}
                  onChange={handleInputChange}
                  required
                  error={!!formErrors.default_currency}
                  label="Default Currency"
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.default_currency && (
                  <Typography variant="caption" sx={{ color: 'error.main', mt: 1, ml: 2, display: 'block' }}>
                    {formErrors.default_currency}
                  </Typography>
                )}
              </StyledSelect>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ position: 'relative' }}>
              <IconWrapper>
                <ScheduleIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Default Payment Terms (days)" 
                name="default_payment_terms"
                type="number"
                variant="outlined"
                fullWidth
                value={companyInfo.default_payment_terms} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.default_payment_terms}
                helperText={formErrors.default_payment_terms}
                disabled={loading}
                placeholder="30"
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ position: 'relative' }}>
              <IconWrapper>
                <DescriptionIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Invoice Prefix" 
                name="invoice_prefix"
                variant="outlined"
                fullWidth
                value={companyInfo.invoice_prefix} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.invoice_prefix}
                helperText={formErrors.invoice_prefix}
                disabled={loading}
                placeholder="INV"
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ position: 'relative' }}>
              <IconWrapper>
                <DescriptionIcon fontSize="medium" />
              </IconWrapper>
              <StyledTextField 
                label="Quote Prefix" 
                name="quote_prefix"
                variant="outlined"
                fullWidth
                value={companyInfo.quote_prefix} 
                onChange={handleInputChange} 
                required
                error={!!formErrors.quote_prefix}
                helperText={formErrors.quote_prefix}
                disabled={loading}
                placeholder="QUO"
              />
            </Box>
          </Grid>
        </Grid>
      )
    }
  ];

  const currentStep = steps[activeStep];
  const progress = ((activeStep + 1) / steps.length) * 100;

  return (
    <PageContainer>
      <Box sx={{ 
        display: { xs: 'block', md: 'flex' }, 
        gap: 3,
        alignItems: 'flex-start', 
        mx: 'auto', 
        width: '100%',
        maxWidth: 1200,
        pt: 2,
        position: 'relative',
        zIndex: 1,
        justifyContent: 'center'
      }}>

        <VerticalProgressBar
          sx={{
            display: { xs: 'none', md: 'flex' },
            mt: 0
          }}
        >
          <VerticalProgressLine progress={(activeStep / (steps.length - 1)) * 100} />
          {steps.map((step, index) => (
            <StepIndicator
              key={index}
              active={index === activeStep}
              completed={index < activeStep}
              onClick={() => index < activeStep && setActiveStep(index)}
              sx={{ cursor: index < activeStep ? 'pointer' : 'default' }}
            >
              <StepCircle active={index === activeStep} completed={index < activeStep}>
                {index < activeStep ? (
                  <CheckIcon />
                ) : (
                  React.cloneElement(step.icon, {
                    sx: {
                      color: index === activeStep ? 'white' : 'inherit',
                      fontSize: 28
                    }
                  })
                )}
              </StepCircle>
              <CustomStepLabel active={index === activeStep} completed={index < activeStep}>
                {step.label}
              </CustomStepLabel>
            </StepIndicator>
          ))}
        </VerticalProgressBar>
        
        <StyledPaper>
          <Fade in timeout={1000}>
            <Box>
              <GlowWorksHeader sx={{ mb: 4 }}>
                <Typography className="logo-text">
                  GlowWorks
                </Typography>
              </GlowWorksHeader>
              
              <HeaderCard>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, position: 'relative', zIndex: 1 }}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 3, 
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }}
                  >
                    <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 500, color: 'text.primary', mb: 1, fontSize: '2.5rem' }}>
                      Company Setup
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '1.1rem' }}>
                      Setup Your Business Profile
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ 
                  color: 'text.secondary', 
                  maxWidth: 600, 
                  ml: 8, 
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  position: 'relative', 
                  zIndex: 1 
                }}>
                  Let's get your business set up for invoicing, financial management, and supply chain operations. This will only take a few minutes.
                </Typography>
              </HeaderCard>

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 4, 
                    borderRadius: 3,
                    fontSize: '1rem',
                    fontWeight: 500,
                    border: `1px solid ${theme.palette.error.main}`,
                    '& .MuiAlert-icon': {
                      fontSize: '1.5rem'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}

              <AnimatedStepContent key={activeStep}>
                <StepCard active>
                  <CardContent sx={{ p: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                      <Box sx={{ 
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 3,
                        p: 1.5,
                        mr: 3
                      }}>
                        {React.cloneElement(steps[activeStep].icon, { 
                          fontSize: 'large',
                          sx: { color: 'primary.main' }
                        })}
                      </Box>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 500, mb: 1, fontSize: '1.8rem' }}>
                          {steps[activeStep].label}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                          {steps[activeStep].description}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {steps[activeStep].fields}
                  </CardContent>
                </StepCard>
              </AnimatedStepContent>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 5 }}>
                <BackButton
                  onClick={activeStep === 0 ? onCancel : handleBack}
                  startIcon={<ArrowBackIcon />}
                >
                  {activeStep === 0 ? 'Cancel' : 'Back'}
                </BackButton>
                
                {activeStep === steps.length - 1 ? (
                  <AnimatedButton
                    onClick={handleCreateCompany}
                    disabled={loading || !isStepValid(activeStep)}
                    sx={{ px: 8 }}
                  >
                    {loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} color="inherit" />
                        Creating Company...
                      </Box>
                    ) : (
                      <>
                        Create Company
                        <CheckIcon sx={{ ml: 1 }} />
                      </>
                    )}
                  </AnimatedButton>
                ) : (
                  <AnimatedButton
                    onClick={handleNext}
                    disabled={!isStepValid(activeStep)}
                    sx={{ px: 8 }}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Continue
                  </AnimatedButton>
                )}
              </Box>
            </Box>
          </Fade>
        </StyledPaper>
      </Box>
    </PageContainer>
  );
};

export default CompanyCreationPage;